import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import React from "react";
import "./Checkout.css";
import "../ShoppingCart/ShoppingCart.css";
import {
  DeliveryMethod,
  useShoppingCart,
} from "../context/ShoppingCartContext";
import CheckoutProductsPanel from "./CheckoutProductsPanel";
import CheckoutBuyerPanel from "./CheckoutBuyerPanel";
import CheckoutDeliveryPanel from "./CheckoutDeliveryPanel";
import CheckoutPaymentPanel from "./CheckoutPaymentPanel";
import BuyerModal from "./BuyerModal";
import CheckoutDeliveryModal from "./CheckoutDeliveryModal";
import { getCheckoutMode, isCheckoutReadOnly } from "./checkoutMode";
import {
  CheckoutConfigContext,
  filterDeliveryMethodsForConfig,
  isDeliveryDisabledByFixedNone,
  getSyncOrderCheckoutContext,
  parseCheckoutConfig,
} from "./checkoutConfig";
import { useCheckoutFixedDelivery } from "./useCheckoutFixedDelivery";
import { CheckoutComponent } from "../interfaces/base";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAppSelector } from "../../../store/hooks";
import RenderChildren from "../RenderChildren";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { getPageLocaleUrl } from "../utilities/helpers";
import { matchesEditorTarget } from "../utilities/componentIdentity";
import usePageLanguage from "../hooks/usePageLanguage";

// Define the steps that need to be completed in order
type CheckoutStep = "buyer" | "delivery" | "payment" | "confirm";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

const colorValue = getComputedStyle(document.documentElement).getPropertyValue(
  "--shop-theme-color"
);

const theme = createTheme({
  palette: {
    primary: {
      main: colorValue ? `${colorValue}` : "#359E3F",
    },
  },
});

const languageTranslations = {
  et: {
    loading: "Laen tellimuse andmeid...",
  },
  en: {
    loading: "Loading order details...",
  },
};

export default function Checkout({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: CheckoutComponent) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [modalDeliveryMethod, setModalDeliveryMethod] =
    useState<DeliveryMethod | null>(null);
  const [isResolvingCheckoutState, setIsResolvingCheckoutState] =
    useState(false);

  // State for sequential modal flow
  const [pendingSteps, setPendingSteps] = useState<CheckoutStep[]>([]);
  const bootstrapSyncStartedRef = useRef(false);

  const pages = useAppSelector((state) => state.builder.pages.list);
  const navigate = useNavigate();
  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const pageId = useAppSelector((state) => state.builder.pages.active);
  const currentPage = useAppSelector((state) => state.builder.pages.list).find(
    (x) => x.uuid === pageId
  );
  const locale = usePageLanguage();

  const {
    confirmedOrder,
    cartItems,
    syncOrder,
    deliveryMethods,
    paymentMethod,
    order,
    cartItemsConfirmed,
    deliveryConfirmed,
    catalogProductsLoaded,
    setBuyer,
  } = useShoppingCart();

  const checkoutConfig = useMemo(
    () => parseCheckoutConfig(attributes),
    [attributes]
  );
  const deliveryConfig = checkoutConfig.delivery;
  const requiresDeliverySelection =
    !isDeliveryDisabledByFixedNone(deliveryConfig);

  const checkoutMode = useMemo(
    () =>
      getCheckoutMode({
        confirmedOrder,
        cartItemsConfirmed,
        paymentMethod,
        orderDelivery: order?.delivery ?? null,
        deliveryConfirmed,
        deliveryRequired: requiresDeliverySelection,
        paymentRequired: checkoutConfig.payment.visible,
        currentPageType: currentPage?.pageType ?? null,
      }),
    [
      confirmedOrder,
      cartItemsConfirmed,
      paymentMethod,
      order?.delivery,
      deliveryConfirmed,
      requiresDeliverySelection,
      currentPage?.pageType,
    ]
  );

  const readOnly = isCheckoutReadOnly(checkoutMode);
  const checkoutContext = useMemo(
    () => getSyncOrderCheckoutContext(deliveryConfig),
    [deliveryConfig]
  );
  const includeB2bContactPerson = checkoutConfig.b2bContactPerson.visible;
  const filteredDeliveryMethods = useMemo(
    () => filterDeliveryMethodsForConfig(deliveryMethods, deliveryConfig),
    [deliveryMethods, deliveryConfig]
  );
  const showDeliveryPanel =
    deliveryConfig.visible && !isDeliveryDisabledByFixedNone(deliveryConfig);

  useCheckoutFixedDelivery(
    deliveryConfig,
    readOnly,
    isResolvingCheckoutState,
    includeB2bContactPerson
  );

  useEffect(() => {
    if (includeB2bContactPerson) {
      return;
    }
    setBuyer((prev) => {
      if (!prev.representativeFirstname && !prev.representativeSurname) {
        return prev;
      }
      return {
        ...prev,
        representativeFirstname: "",
        representativeSurname: "",
      };
    });
  }, [includeB2bContactPerson, setBuyer]);

  // Handle cancel flow - clear pending steps when user closes a modal via X button
  const cancelFlow = useCallback(() => {
    setPendingSteps([]);
  }, []);

  // Handler for buyer modal cancel (X button clicked)
  const handleBuyerModalCancel = useCallback(() => {
    setShowBuyerModal(false);
    cancelFlow();
  }, [cancelFlow]);

  // Handler for delivery modal cancel (X button clicked)
  const handleDeliveryModalCancel = useCallback(() => {
    setShowDeliveryModal(false);
    cancelFlow();
  }, [cancelFlow]);

  useEffect(() => {
    if (bootstrapSyncStartedRef.current) {
      return;
    }
    if (!catalogProductsLoaded) {
      return;
    }

    const isOrderCompletePage = currentPage?.pageType === "order-complete";
    const hasConfirmedOrder = !!confirmedOrder?.uuid;
    const hasRecoverableOrderState = !!order?.uuid || cartItems.length > 0;

    // Do not recreate/reload checkout state on order-complete when there is
    // nothing left to recover. This prevents accidental fresh /order posts.
    if (isOrderCompletePage && (!hasRecoverableOrderState || hasConfirmedOrder)) {
      return;
    }

    if (isOrderCompletePage) {
      bootstrapSyncStartedRef.current = true;
      setIsResolvingCheckoutState(true);
      syncOrder({ checkoutContext, includeB2bContactPerson }).finally(() => {
        setIsResolvingCheckoutState(false);
      });
      return;
    }

    if (!hasConfirmedOrder && hasRecoverableOrderState) {
      bootstrapSyncStartedRef.current = true;
      setIsResolvingCheckoutState(true);
      syncOrder({ checkoutContext, includeB2bContactPerson }).finally(() => {
        setIsResolvingCheckoutState(false);
      });
    }
  }, [
    currentPage?.pageType,
    confirmedOrder?.uuid,
    order?.uuid,
    cartItems.length,
    catalogProductsLoaded,
    checkoutContext,
    syncOrder,
    includeB2bContactPerson,
  ]);

  useEffect(() => {
    if (
      confirmedOrder &&
      confirmedOrder.uuid &&
      currentPage?.pageType !== "order-complete"
    ) {
      const page = pages.find((p) => p.pageType === "order-complete");
      if (page) {
        navigate(getPageLocaleUrl(page, languages));
      }
    }
  }, [confirmedOrder, currentPage?.pageType, pages, languages, navigate]);

  const showLoading =
    isResolvingCheckoutState &&
    (currentPage?.pageType === "order-complete" || !readOnly);

  const isHorizontalLayout =
    attributes && attributes.layout === "horizontal";

  return (
    <CheckoutConfigContext.Provider value={checkoutConfig}>
    <ThemeProvider theme={theme}>
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes && attributes.styles}
        className={
          attributes &&
          attributes.classes +
            (attributes.classes != "container" &&
            attributes.classes != "container-fluid"
              ? " builder-component builder-component__checkout"
              : "") +
            (matchesEditorTarget({ id, uuid }, editorId)
              ? " builder-component--active"
              : "")
        }
        data-component-type="section"
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />
        {showLoading ? (
          <div
            className="page animsition withoutsidemenu checkout"
            style={{
              animationDuration: "800ms",
              opacity: "1",
            }}
          >
            <div
              className="page-content d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: "260px" }}
            >
              <div className="d-flex align-items-center justify-content-center">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">
                    {languageTranslations[locale]?.loading ||
                      languageTranslations.et.loading}
                  </span>
                </div>
                {languageTranslations[locale]?.loading ||
                  languageTranslations.et.loading}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="page animsition withoutsidemenu checkout"
            style={{
              animationDuration: "800ms",
              opacity: "1",
            }}
          >
            <div className="page-content  padding-top-0 ">
              <div className="row">
                <div
                  className={isHorizontalLayout ? "col-lg-12" : "col-lg-7"}
                >
                  <CheckoutProductsPanel
                    id={id}
                    readOnly={readOnly}
                    checkoutMode={checkoutMode}
                  />
                </div>
                <div
                  className={isHorizontalLayout ? "col-lg-12" : "col-lg-5"}
                >
                  <CheckoutBuyerPanel
                    readOnly={readOnly}
                    setShowBuyerModal={setShowBuyerModal}
                  />
                  {showDeliveryPanel && (
                    <CheckoutDeliveryPanel
                      readOnly={readOnly}
                      setModalDeliveryMethod={setModalDeliveryMethod}
                      setShowDeliveryModal={setShowDeliveryModal}
                    />
                  )}
                  <CheckoutPaymentPanel
                    readOnly={readOnly}
                    showPaymentBlock={checkoutConfig.payment.visible}
                    setShowBuyerModal={setShowBuyerModal}
                    setModalDeliveryMethod={setModalDeliveryMethod}
                    setShowDeliveryModal={setShowDeliveryModal}
                    showDeliveryModal={showDeliveryModal}
                    showBuyerModal={showBuyerModal}
                    pendingSteps={pendingSteps}
                    setPendingSteps={setPendingSteps}
                    onBuyerModalClose={handleBuyerModalCancel}
                  />
                </div>
              </div>
            </div>
            {!readOnly && showBuyerModal && (
              <BuyerModal
                show={showBuyerModal}
                setShow={setShowBuyerModal}
                onCancel={handleBuyerModalCancel}
              />
            )}
            {!readOnly && showDeliveryModal && (
              <CheckoutDeliveryModal
                deliveryMethods={filteredDeliveryMethods}
                deliveryMethod={modalDeliveryMethod}
                show={showDeliveryModal}
                setShow={setShowDeliveryModal}
                onCancel={handleDeliveryModalCancel}
              />
            )}
          </div>
        )}
      </Div>
    </ThemeProvider>
    </CheckoutConfigContext.Provider>
  );
}
